import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './Components/common/ProtectedRoute';
import Layout from './Components/layout/Layout';
import { ConfirmProvider } from './Components/common/ConfirmDialog';
import { LoadingSpinner } from './Components/common/Components';

// Auth Pages (eager: first screen for signed-out users)
import Login from './Pages/Auth/Login';

import './App.css';

// Every other page is a separate chunk, downloaded the first time it is opened
const Dashboard = lazy(() => import('./Pages/Dashboard/Dashboard'));
const EmployeeList = lazy(() => import('./Pages/Employees/EmployeeList'));
const EmployeeProfile = lazy(() => import('./Pages/Employees/EmployeeProfile'));
const AttendanceList = lazy(() => import('./Pages/Attendance/AttendanceList'));
const AttendanceRecord = lazy(() => import('./Pages/Attendance/AttendanceRecord'));
const LeaveList = lazy(() => import('./Pages/Leaves/LeaveList'));
const LeaveApplication = lazy(() => import('./Pages/Leaves/LeaveApplication'));
const Holidays = lazy(() => import('./Pages/Holidays/Holidays'));
const OnsiteRequest = lazy(() => import('./Pages/OnSiteRequest/OnsiteRequest'));
const TimesheetList = lazy(() => import('./Pages/Timesheets/TimesheetList'));
const TimesheetEntry = lazy(() => import('./Pages/Timesheets/TimesheetEntry'));
const TaskList = lazy(() => import('./Pages/Tasks/TaskList'));
const TaskDetail = lazy(() => import('./Pages/Tasks/TaskDetail'));
const AssetList = lazy(() => import('./Pages/Assets/AssetList'));
const AssetDetail = lazy(() => import('./Pages/Assets/AssetDetail'));
const AnnouncementList = lazy(() => import('./Pages/Announcements/AnnouncementList'));
const BlogList = lazy(() => import('./Pages/Blog/BlogList'));
const Reports = lazy(() => import('./Pages/Reports'));
const Masters = lazy(() => import('./Pages/Masters/Masters'));
const ManagerManagement = lazy(() => import('./Pages/ManagerManagement/ManagerManagement'));
const WFHApplication = lazy(() => import('./Pages/WFH/WFHApplication'));
const WFHCombined = lazy(() => import('./Pages/WFH/WFHCombined'));
const ShareDrive = lazy(() => import('./Pages/ShareDrive/ShareDrive'));
const ProjectMappingList = lazy(() => import('./Pages/ProjectMapping/ProjectMappingList'));
const NotFound = lazy(() => import('./Pages/Errors/NotFound'));
const Unauthorized = lazy(() => import('./Pages/Errors/Unauthorized'));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      // Re-opening a tab within 30s reuses cached data; mutations still invalidate explicitly
      staleTime: 30 * 1000,
    },
  },
});

const FullPageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <LoadingSpinner size="lg" message="Loading…" />
  </div>
);

// Role-restricted page inside the shared layout
const RoleRoute = ({ roles, children }) => <ProtectedRoute allowedRoles={roles}>{children}</ProtectedRoute>;

function AppContent() {
  return (
    <Router>
      <div className="App">
        <Suspense fallback={<FullPageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />

            {/* Protected Routes */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* One persistent layout for every signed-in page: navigating no longer remounts
                the shell or refetches its data */}
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route path="/dashboard" element={<Dashboard />} />

              {/* Employee Management - Admin/HR Only */}
              <Route path="/employees" element={<RoleRoute roles={['ADMIN', 'HR']}><EmployeeList /></RoleRoute>} />
              <Route path="/employees/:id" element={<EmployeeProfile />} />

              {/* Attendance Management */}
              <Route path="/attendance" element={<AttendanceList />} />
              <Route path="/attendance/record" element={<RoleRoute roles={['ADMIN', 'HR']}><AttendanceRecord /></RoleRoute>} />

              {/* Leave Management */}
              <Route path="/leaves" element={<LeaveList />} />
              <Route path="/leaves/apply" element={<LeaveApplication />} />

              <Route path="/holidays" element={<Holidays />} />
              <Route path="/onsite-requests" element={<OnsiteRequest />} />

              {/* WFH Management */}
              <Route path="/wfh" element={<WFHCombined />} />
              <Route path="/wfh/apply" element={<WFHApplication />} />
              <Route path="/wfh/management" element={<RoleRoute roles={['ADMIN', 'HR']}><WFHCombined /></RoleRoute>} />

              <Route path="/managers" element={<ManagerManagement />} />
              <Route path="/project-allocation" element={<ProjectMappingList />} />

              {/* Timesheet Management */}
              <Route path="/timesheets" element={<TimesheetList />} />
              <Route path="/timesheets/entry" element={<TimesheetEntry />} />

              {/* Task Management */}
              <Route path="/tasks" element={<TaskList />} />
              <Route path="/tasks/:id" element={<TaskDetail />} />

              {/* Asset Management */}
              <Route path="/assets" element={<AssetList />} />
              <Route path="/assets/:id" element={<AssetDetail />} />

              <Route path="/announcements" element={<AnnouncementList />} />
              <Route path="/blogs" element={<BlogList />} />

              {/* Reports - Admin/HR Only */}
              <Route path="/reports" element={<RoleRoute roles={['ADMIN', 'HR']}><Reports /></RoleRoute>} />
              <Route path="/masters" element={<RoleRoute roles={['ADMIN']}><Masters /></RoleRoute>} />

              {/* Cloud Storage */}
              <Route path="/share-drive" element={<ShareDrive />} />
            </Route>

            {/* Error Pages */}
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>

        <ToastContainer
          position="top-right"
          autoClose={2500}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </Router>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ConfirmProvider>
          <AppContent />
        </ConfirmProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
